import { memo, ReactNode, useCallback } from 'react';
import { Box, Button, SpaceBetween } from '@amzn/awsui-components-core';
import { Epic, Task } from '@amzn/awscat-pattern-library-service-model';

import { SortableList, SortableRenderArgs } from '../v2-common/SortableList';
import { PatternTask } from './PatternTask';
import styles from './PatternTaskList.module.scss';

export interface PatternTaskListProps {
  readonly epic: Epic;
  /** Id of the task that was just added and should start in edit mode, or undefined when idle. */
  readonly newlyAddedTaskId: string | undefined;
  readonly contentId: string;
  readonly onAddTask: () => void;
  readonly onReorderTasks: (newOrderIds: readonly string[]) => void;
}

/**
 * Renders the tasks table inside one EpicContainerView. Columns:
 * Story | Description | Skills required | Actions.
 *
 * Uses a plain HTML table instead of CloudScape Table because
 * CloudScape Table does not support custom drag-and-drop row
 * reordering.
 */
export const PatternTaskList = memo(function PatternTaskList({
  epic,
  newlyAddedTaskId,
  contentId,
  onAddTask,
  onReorderTasks,
}: PatternTaskListProps) {
  const tasks = epic.tasks ?? [];

  const keyExtractor = useCallback((task: Task) => task.id, []);

  const renderTask = useCallback(
    (task: Task, sortableArgs: SortableRenderArgs) => (
      <PatternTask
        key={task.id}
        epicId={epic.id}
        task={task}
        sortableArgs={sortableArgs}
        initiallyEditing={task.id === newlyAddedTaskId}
        contentId={contentId}
      />
    ),
    [epic.id, newlyAddedTaskId, contentId],
  );

  return (
    <SpaceBetween size="s">
      {tasks.length > 0 ? (
        [
          <SortableList
            key="tasks"
            id={`epic-${epic.id}`}
            items={tasks}
            keyExtractor={keyExtractor}
            onReorder={onReorderTasks}
            item={renderTask}
            container={(children: ReactNode) => (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.headerCell}>Story</th>
                    <th className={styles.headerCell}>Description</th>
                    <th className={styles.headerCellSkills}>Skills</th>
                    <th className={styles.headerCellActions}>Actions</th>
                  </tr>
                </thead>
                <tbody>{children}</tbody>
              </table>
            )}
          />,
          <div key="add-task" className={styles.buttonArea}>
            <Button iconName="add-plus" variant="inline-link" onClick={onAddTask}>
              Add story
            </Button>
          </div>,
        ]
      ) : (
        <Box textAlign="center" padding={{ horizontal: 'l', bottom: 'l' }}>
          <Button iconName="add-plus" variant="inline-link" onClick={onAddTask}>
            Add story
          </Button>
        </Box>
      )}
    </SpaceBetween>
  );
});
